import React, { useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Card,
  CardContent,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import AppsIcon from "@mui/icons-material/Apps";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import GroupsIcon from "@mui/icons-material/Groups";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import DashboardLayout from "../../layout/DashboardLayout";
import { brand } from "../../theme";

const FAQ_CATEGORIES = [
  {
    id: "general",
    label: "General Information",
    icon: <InfoOutlinedIcon />,
    color: brand.terracotta,
    bg: brand.terracottaTint,
    border: brand.terracottaLight,
    questions: [
      {
        q: "What is the Client Portal?",
        a: "The Client Portal is a secure platform for registered travel agencies to access post-booking tools, PCC management, reports, and account settings.",
      },
      {
        q: "Who can use the Client Portal?",
        a: "Only authorized agencies and approved staff members can access the portal. Access levels are determined by the agency administrator.",
      },
    ],
  },
  {
    id: "security",
    label: "Login & Security",
    icon: <LockOutlinedIcon />,
    color: brand.black,
    bg: "#fdf4ff",
    border: "#e9d5ff",
    questions: [
      {
        q: "I forgot my password. What should I do?",
        a: "Click \"Forgot Password\" on the login page and follow the instructions. If the issue persists, contact Helpdesk support.",
      },
      {
        q: "How do I update my profile or change my password?",
        a: "Navigate to Profile → Account Settings to update your information or password.",
      },
    ],
  },
  {
    id: "pcc",
    label: "PCC Requests",
    icon: <FlightTakeoffIcon />,
    color: "#d97706",
    bg: "#fffbeb",
    border: "#fde68a",
    questions: [
      {
        q: "How do I request a PCC?",
        a: "Go to Request PCC and click \"Submit PCC Request\". The Helpdesk team will process your request.",
      },
      {
        q: "How long does PCC approval take?",
        a: "PCC requests are typically processed within 24–48 business hours.",
      },
    ],
  },
  {
    id: "products",
    label: "Product Access & Requests",
    icon: <AppsIcon />,
    color: "#16a34a",
    bg: "#f0fdf4",
    border: "#bbf7d0",
    questions: [
      {
        q: "How do I request access to a product?",
        a: "Go to Explore Products and select the product you want. Click \"Request Product\" to submit your activation request.",
      },
      {
        q: "Can I request a demo?",
        a: "Yes. Click \"Request Demo\" on any product card and a Helpdesk agent will contact you to schedule a demonstration.",
      },
    ],
  },
  {
    id: "billing",
    label: "Billing & Subscription",
    icon: <ReceiptLongIcon />,
    color: "#dc2626",
    bg: "#fff1f2",
    border: "#fecdd3",
    questions: [
      {
        q: "How does billing work?",
        a: "Billing depends on the specific product. Invoices and billing notifications will be sent to your agency administrator.",
      },
      {
        q: "Where can I download invoices?",
        a: "Navigate to Billing → Invoices to download or review payment history.",
      },
    ],
  },
  {
    id: "support",
    label: "Technical Support",
    icon: <SupportAgentIcon />,
    color: brand.goldDark,
    bg: "#FBF3D9",
    border: brand.goldLight,
    questions: [
      {
        q: "What should I do if I encounter an error?",
        a: "Take a screenshot and contact the Helpdesk through Support → Create Ticket.",
      },
      {
        q: "Which browsers are supported?",
        a: "The Client Portal works best on Chrome, Edge, and Firefox.",
      },
    ],
  },
  {
    id: "agency",
    label: "Agency & User Management",
    icon: <GroupsIcon />,
    color: "#9333ea",
    bg: "#faf5ff",
    border: "#d8b4fe",
    questions: [
      {
        q: "How do I add or remove staff members?",
        a: "Agency admins can manage staff under Agency Settings → User Management.",
      },
      {
        q: "Can staff have different access levels?",
        a: "Yes. Roles determine the features and tools each user can access.",
      },
    ],
  },
];

function CategorySection({ category }) {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ p: 3 }}>
        {/* Category header */}
        <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              bgcolor: category.bg,
              border: `1px solid ${category.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: category.color,
              "& svg": { fontSize: "1.1rem" },
              flexShrink: 0,
            }}
          >
            {category.icon}
          </Box>
          <Typography variant="h6" color="#0f172a">
            {category.label}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Accordions */}
        <Box>
          {category.questions.map((item, i) => (
            <Accordion
              key={i}
              expanded={expanded === i}
              onChange={handleChange(i)}
              disableGutters
              elevation={0}
              sx={{
                border: "none",
                borderLeft: expanded === i ? `3px solid ${category.color}` : "3px solid transparent",
                borderRadius: "0 !important",
                transition: "border-color 0.2s ease",
                mb: 0.5,
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: 0 },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#94a3b8", fontSize: "1.1rem" }} />}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  minHeight: 48,
                  borderRadius: "8px",
                  "&:hover": { bgcolor: "#f8fafc" },
                  "& .MuiAccordionSummary-content": { my: 1 },
                }}
              >
                <Typography variant="body2" fontWeight={500} color="#0f172a">
                  {item.q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                <Typography variant="body2" color="#64748b" sx={{ lineHeight: 1.7 }}>
                  {item.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function FAQPage() {
  return (
    <DashboardLayout>
      <Box>
        {/* Hero banner */}
        <Box
          sx={{
            background: brand.black,
            borderRadius: "14px",
            p: { xs: 3, sm: 4 },
            mb: 4,
            color: "#fff",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5} mb={1}>
            <HeadsetMicIcon sx={{ fontSize: "1.4rem", opacity: 0.9 }} />
            <Typography variant="h5" sx={{ color: "#fff" }}>
              Help & FAQ
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.8)", maxWidth: 560 }}
          >
            Find answers to common questions about using the Client Portal —
            from account setup and PCC requests to product access and billing.
          </Typography>
        </Box>

        {/* Category sections */}
        {FAQ_CATEGORIES.map((cat) => (
          <CategorySection key={cat.id} category={cat} />
        ))}

        {/* Contact CTA */}
        <Card
          sx={{
            mt: 3,
            background: brand.terracottaTint,
            border: `1px solid ${brand.terracottaLight}`,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={1}>
              <SupportAgentIcon sx={{ color: brand.terracotta, fontSize: "1.2rem" }} />
              <Typography variant="h6" color="#0f172a">
                Still need help?
              </Typography>
            </Box>
            <Typography variant="body2" color="#64748b" mb={2.5}>
              Our Helpdesk team is available Monday–Friday, 9:00 AM to 5:00 PM
              (WAT). Reach us by email or submit a support ticket.
            </Typography>
            <Box display="flex" gap={1.5} flexWrap="wrap">
              <Button
                variant="contained"
                href="mailto:support@sabrecwa.com"
                component="a"
              >
                Email Support
              </Button>
              <Button variant="outlined">Create Ticket</Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </DashboardLayout>
  );
}
